import { t } from "../../i18n";
import { useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Box, Button, ButtonBase, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Stack, Tooltip, Typography } from '@mui/material';
import PhotoCameraOutlined from '@mui/icons-material/PhotoCameraOutlined';
import { useAuth } from '../../auth/AuthProvider';
import { uploadOwnPicture } from '../../services/profile';
import { PROFILE_PICTURE_ACCEPT, validateProfilePicture } from '../../utils/profilePicture';
export default function ProfilePhoto() {
  const {
    user,
    updateUserPicture
  } = useAuth();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [success, setSuccess] = useState(false);
  const initials = (user.name || user.email || '').trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();
  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);
  const selectImage = event => {
    const selected = event.target.files?.[0];
    event.target.value = '';
    if (!selected) return;
    const validationError = validateProfilePicture(selected);
    setError(validationError);
    setPreviewError(false);
    setFile(validationError ? null : selected);
    setPreview(validationError ? null : URL.createObjectURL(selected));
    setOpen(true);
  };
  const close = () => {
    if (busy) return;
    setOpen(false);
    setFile(null);
    setPreview(null);
    setError(null);
    setPreviewError(false);
  };
  const save = async () => {
    if (!file || previewError) return;
    setBusy(true);
    setError(null);
    try {
      const picture = await uploadOwnPicture(file);
      updateUserPicture(picture, user.uuid);
      setOpen(false);
      setFile(null);
      setPreview(null);
      setSuccess(true);
    } catch (uploadError) {
      const status = uploadError?.response?.status;
      setError(status === 413 ? 'A imagem excede o tamanho permitido pelo servidor.' : status === 401 ? 'A sessão expirou. Volte a iniciar sessão.' : 'Não foi possível guardar a fotografia. Tente novamente.');
    } finally {
      setBusy(false);
    }
  };
  return <>
            <Tooltip title={t("Alterar fotografia de perfil")}>
                <ButtonBase aria-label={t("Alterar fotografia de perfil")} onClick={() => inputRef.current?.click()} disabled={busy} sx={{
        borderRadius: '50%',
        flexShrink: 0,
        border: '3px solid',
        borderColor: 'primary.main',
        position: 'relative',
        '&:hover .photo-overlay, &.Mui-focusVisible .photo-overlay': {
          opacity: 1
        }
      }}>
                    <Avatar src={user.picture || undefined} alt={user.name || ''} sx={{
          width: 80,
          height: 80,
          bgcolor: 'primary.main',
          color: '#fff',
          fontSize: '1.75rem'
        }}>
                        {initials}
                    </Avatar>
                    <Box className="photo-overlay" sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          bgcolor: 'rgba(16, 59, 102, 0.65)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0,
          transition: 'opacity 150ms'
        }}>
                        <PhotoCameraOutlined aria-hidden="true" />
                    </Box>
                    <Box sx={{
          position: 'absolute',
          bottom: -2,
          right: -2,
          p: 0.5,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: '#fff',
          border: '2px solid white',
          display: 'flex'
        }}>
                        <PhotoCameraOutlined aria-hidden="true" sx={{
            fontSize: 16
          }} />
                    </Box>
                </ButtonBase>
            </Tooltip>
            <input ref={inputRef} type="file" accept={PROFILE_PICTURE_ACCEPT} hidden aria-label={t("Selecionar fotografia de perfil")} onChange={selectImage} />
            <Dialog open={open} onClose={close} fullWidth maxWidth="xs" aria-labelledby="photo-dialog-title">
                <DialogTitle id="photo-dialog-title">{t("Alterar fotografia")}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} alignItems="center" sx={{
          pt: 1
        }}>
                        {preview && <Box component="img" src={preview} alt={t("Pré-visualização da nova fotografia")} onError={() => {
            setPreviewError(true);
            setError(t('Não foi possível abrir esta imagem. Escolha outra.'));
          }} sx={{
            width: 180,
            height: 180,
            objectFit: 'cover',
            borderRadius: '50%'
          }} />}
                        {file && <Typography variant="body2" sx={{
            maxWidth: '100%',
            overflowWrap: 'anywhere'
          }}>{file.name}</Typography>}
                        <Typography variant="body2" color="text.secondary">{t("JPG, PNG ou WebP, até 5 MB.")}</Typography>
                        <Button type="button" onClick={() => inputRef.current?.click()} disabled={busy}>{t("Escolher outra imagem")}</Button>
                        {t(error) && <Alert severity="error" sx={{
            width: '100%'
          }}>{t(error)}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{
        px: 3,
        pb: 3
      }}>
                    <Button type="button" onClick={close} disabled={busy}>{t("Cancelar")}</Button>
                    <Button type="button" variant="contained" onClick={save} disabled={busy || !file || previewError} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}>
                        {busy ? t('A guardar…') : t('Guardar fotografia')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={success} autoHideDuration={6000} onClose={() => setSuccess(false)}>
                <Alert severity="success" onClose={() => setSuccess(false)}>{t("A fotografia de perfil foi atualizada.")}</Alert>
            </Snackbar>
        </>;
}
